import React, { useState } from "react";

// Shared bits for the Pallets view and the pallet window

export const HEALTH_LABEL = { red: "Needs action", amber: "Watch", green: "On track" };
const HEALTH_COLOR = { red: "#EF4444", amber: "#F59E0B", green: "#10B981" };

export const LEG_LABEL = {
    notStarted: "Not started", planned: "Planned", booked: "Booked", awaitingBooking: "Awaiting booking",
    awaitingCollection: "Awaiting collection", inTransit: "In transit", atDepotAwaitingBooking: "At depot · awaiting booking",
    atDepotBooked: "At depot · booked", outForDelivery: "Out for delivery", delivered: "Delivered", returned: "Returned", exception: "Exception",
};
export const MS_TONE = { done: "good", overdue: "bad", dueSoon: "warn", upcoming: "muted", unknown: "muted", info: "muted" };
export const CC_ICON = { pass: "✓", fail: "✕", warn: "!", notApplicable: "–" };
export const CC_TONE = { pass: "good", fail: "bad", warn: "warn", notApplicable: "muted" };
export const SOURCE_LABEL = { palletProgram: "Pallet sheet", inbound: "Pilot report", outbound: "Carrier report", platform: "Platform" };

const TONE = {
    good:  "bg-green-500/15 text-green-400 border-green-500/30",
    bad:   "bg-red-500/15 text-red-400 border-red-500/30",
    warn:  "bg-amber-500/15 text-amber-400 border-amber-500/30",
    info:  "bg-blue-500/15 text-blue-300 border-blue-500/30",
    muted: "bg-slate-700/60 text-slate-300 border-slate-600",
};

export function Chip({ tone = "muted", title, children }) {
    return <span title={title} className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-semibold whitespace-nowrap ${TONE[tone] || TONE.muted}`}>{children}</span>;
}

export function Dot({ health }) {
    return <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: HEALTH_COLOR[health] || "#6B7280" }}
        title={HEALTH_LABEL[health]} aria-label={HEALTH_LABEL[health]}/>;
}

export const Mono = ({ children }) => <span className="font-mono">{children}</span>;
export const Sub = ({ children, className = "" }) => <div className={`text-[11px] text-slate-500 ${className}`}>{children}</div>;

// Dates: "12 Oct", with the year only when it isn't the report's year
const toDate = s => (s ? new Date(s.length === 10 ? `${s}T00:00:00` : s) : null);
export function fmtD(s, refYear) {
    const d = toDate(s);
    if (!d || isNaN(d)) return "—";
    const o = { day: "numeric", month: "short" };
    if (refYear && d.getFullYear() !== refYear) o.year = "numeric";
    return d.toLocaleDateString("en-AU", o);
}
export function fmtDT(s) {
    const d = toDate(s);
    if (!d || isNaN(d)) return "—";
    return `${d.toLocaleDateString("en-AU", { day: "numeric", month: "short" })} ${d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}`;
}
export function relDays(s, reportDate) {
    const d = toDate(s), r = toDate(reportDate);
    if (!d || !r) return "";
    const n = Math.round((d - r) / 86400000);
    return n === 0 ? "today" : n > 0 ? `in ${n}d` : `${-n}d ago`;
}

export const uniq = a => [...new Set(a.filter(Boolean))];
export const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
export const isPlaceholder = e => /@example\.com$/i.test(e);

// Email address list with chips, typing / pasting several at once and saved suggestions
export function EmailChips({ id, label, value, onChange, suggestions = [], placeholder }) {
    const [draft, setDraft] = useState("");
    const [err, setErr] = useState("");
    const add = raw => {
        const parts = raw.split(/[\s,;]+/).map(x => x.trim()).filter(Boolean);
        const bad = parts.filter(p => !EMAIL_RE.test(p));
        const good = parts.filter(p => EMAIL_RE.test(p));
        if (good.length) onChange(uniq([...value, ...good]));
        setErr(bad.length ? `Not an email address: ${bad.join(", ")}` : "");
        setDraft(bad.join(" "));
    };
    const sug = suggestions.filter(x => !value.includes(x) && (!draft || x.toLowerCase().includes(draft.toLowerCase()))).slice(0, 6);
    return (
        <div className="space-y-1">
            <label htmlFor={id} className="text-[11px] text-slate-400">{label}</label>
            <div className="flex flex-wrap items-center gap-1 bg-[#0D1520] border border-[#1F2937] rounded-lg px-2 py-1.5 focus-within:border-blue-500/50">
                {value.map(e => (
                    <span key={e} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] ${isPlaceholder(e) ? "bg-amber-500/15 text-amber-300" : "bg-[#1F2937] text-slate-200"}`}
                        title={isPlaceholder(e) ? "Placeholder address — replace under Notification contacts" : undefined}>
                        {e}
                        <button type="button" aria-label={`Remove ${e}`} className="text-slate-500 hover:text-white" onClick={() => onChange(value.filter(x => x !== e))}>×</button>
                    </span>
                ))}
                <input id={id} value={draft} placeholder={value.length ? "" : placeholder}
                    className="flex-1 min-w-[120px] bg-transparent text-[12px] text-slate-200 placeholder:text-slate-600 outline-none"
                    onChange={e => { setDraft(e.target.value); setErr(""); }}
                    onKeyDown={e => {
                        if (["Enter", ",", ";", "Tab"].includes(e.key) && draft.trim()) { e.preventDefault(); add(draft); }
                        else if (e.key === "Backspace" && !draft && value.length) onChange(value.slice(0, -1));
                    }}
                    onBlur={() => draft.trim() && add(draft)}/>
            </div>
            {err && <p className="text-[11px] text-red-400">{err}</p>}
            {sug.length > 0 && (
                <div className="flex flex-wrap items-center gap-1">
                    <span className="text-[10px] text-slate-600">Saved:</span>
                    {sug.map(x => (
                        <button type="button" key={x} onClick={() => onChange(uniq([...value, x]))}
                            className="px-1.5 py-0.5 rounded text-[10px] border border-[#1F2937] text-slate-400 hover:text-white hover:border-slate-500">+ {x}</button>
                    ))}
                </div>
            )}
        </div>
    );
}

// Seven-stage baton track: done → current (with days late) → pending
export function BatonTrack({ custody, compact }) {
    if (!custody?.stages) return null;
    return (
        <ol className={`flex ${compact ? "gap-0.5" : "gap-1"}`} aria-label="Process stages">
            {custody.stages.map(st => {
                const bar = st.status === "done" ? "bg-green-500/70" : st.status === "current" ? (st.daysLate > 0 ? "bg-red-500" : "bg-blue-500") : "bg-[#1F2937]";
                return (
                    <li key={st.key} className={`flex-1 min-w-0 ${compact ? "" : "space-y-1"}`} aria-current={st.status === "current" ? "step" : undefined}
                        title={`${st.label} · ${st.team}`}>
                        <span className={`block rounded-full ${compact ? "h-1.5" : "h-2"} ${bar}`}/>
                        {!compact && (
                            <>
                                <span className={`block text-[10px] font-semibold truncate ${st.status === "current" ? "text-white" : "text-slate-500"}`}>
                                    {st.status === "current" ? custody.sitsWith : st.team}
                                </span>
                                <span className="block text-[10px] text-slate-500 leading-tight">{st.label}</span>
                                {st.status === "current" && st.daysLate > 0 && <span className="block text-[10px] font-bold text-red-400">{st.daysLate}d late</span>}
                                {st.status === "done" && <span className="block text-[10px] text-green-400">Done</span>}
                            </>
                        )}
                    </li>
                );
            })}
        </ol>
    );
}
